import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { AsyncReturnService } from '../../../../../shared';
import { CartEntryReamingConnector } from '../../connector/cart-entry-reaming.connector';
import { CartEntryReamingActionTypes } from '../actions/cart-entry-reaming.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryReamingEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  addReaming$: Observable<CartActions.AddCartEntryReamingSuccess | CartActions.AddCartEntryReamingFail | CartActions.LoadCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartEntryReamingActionTypes.AddCartEntryReaming),
        map((action: CartActions.AddCartEntryReaming) => action.payload),
        concatMap(({ actionToken, ...payload }) =>
          this.connector.add(payload.userId, payload.cartId, payload.entry).pipe(
            map(() => {
              this.asyncReturnService.resolveToken(actionToken, true);
              return new CartActions.AddCartEntryReamingSuccess({
                ...payload,
              });
            }),
            catchError((error) => {
              this.asyncReturnService.resolveToken(actionToken, false);
              return from([
                new CartActions.AddCartEntryReamingFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new CartActions.LoadCart({
                  userId: payload.userId,
                  cartType: payload.cartType,
                  cartId: payload.cartId,
                }),
              ]);
            })
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleSuccessForAddReaming$: Observable<CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReamingActionTypes.AddCartEntryReamingSuccess),
      map((action: CartActions.AddCartEntryReamingSuccess) => action.payload),
      map(
        (payload) =>
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          })
      )
    )
  );

  handleFailureForAddReaming$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReamingActionTypes.AddCartEntryReamingFail),
      map((action: CartActions.AddCartEntryReamingFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.addCartEntryReamingFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeReaming$: Observable<
    CartActions.RemoveCartEntryReamingSuccess | CartActions.RemoveCartEntryReamingFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReamingActionTypes.RemoveCartEntryReaming),
      map((action: CartActions.RemoveCartEntryReaming) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.remove(payload.userId, payload.cartId, payload.entry).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.RemoveCartEntryReamingSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.RemoveCartEntryReamingFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForRemoveReaming$: Observable<CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReamingActionTypes.RemoveCartEntryReamingSuccess),
      map((action: CartActions.RemoveCartEntryReamingSuccess) => action.payload),
      map(
        (payload) =>
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          })
      )
    )
  );

  handleFailureForRemoveReaming$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryReamingActionTypes.RemoveCartEntryReamingFail),
      map((action: CartActions.RemoveCartEntryReamingFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.removeCartEntryReamingFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartEntryReamingConnector,
    private asyncReturnService: AsyncReturnService
  ) {}
}
